import axios from 'axios'
import { Autocomplete, AutocompleteProps, MenuItem, TextField, TextFieldProps } from '@mui/material'
import { CatalogProduct, CatalogProductQuery } from '../types'
import { useEffect, useState } from 'react'
import { getCatalogProducts } from '../services/catalogProductService'
import { useTranslation } from 'react-i18next'

const EMPTY_QUERY: CatalogProductQuery = {}

export interface CatalogProductManufcaturerAutocompleteProps extends Omit<
  AutocompleteProps<string, false, false, true>,
  'options' | 'value' | 'renderInput' | 'groupBy'
> {
  query?: CatalogProductQuery
  catalogProducts?: CatalogProduct[]
  value?: string | null
  label?: string
  placeholder?: string
  error?: TextFieldProps['error']
  helperText?: TextFieldProps['helperText']
}

export default function CatalogProductManufacturerAutocomplete({
  query = EMPTY_QUERY,
  catalogProducts: initialCatalogProducts,
  value,
  label,
  placeholder,
  error,
  helperText,
  inputValue,
  ...props
}: CatalogProductManufcaturerAutocompleteProps) {
  const { t } = useTranslation()

  const [catalogProducts, setCatalogProducts] = useState<CatalogProduct[]>([])

  const [hasLoadError, setHasLoadError] = useState(false)

  const hasHelperText =
    helperText !== undefined && helperText !== null && helperText !== false && helperText !== ''

  const resolvedError = Boolean(error) || (!hasHelperText && hasLoadError)

  const resolvedHelperText = hasHelperText
    ? helperText
    : hasLoadError
      ? t('actions:loadError')
      : helperText

  const sortedOptions = makeOptions(catalogProducts)

  function makeOptions(catalogProducts: CatalogProduct[]) {
    return [
      ...new Set(
        catalogProducts
          .filter((product) => product.manufacturer.length > 0)
          .map((product) => product.manufacturer)
      )
    ].sort((a, b) => a.localeCompare(b))
  }

  /**
   * Load the catalog products.
   */
  async function loadCatalogProducts(query: CatalogProductQuery, controller?: AbortController) {
    try {
      setHasLoadError(false)
      setCatalogProducts(await getCatalogProducts(query, { signal: controller?.signal }))
    } catch (error) {
      if (axios.isCancel(error)) {
        return
      }

      setHasLoadError(true)
      setCatalogProducts([])
    }
  }

  /**
   * Load the catalog products when the component mounts unless options are provided.
   */
  useEffect(() => {
    if (initialCatalogProducts === undefined) {
      const controller = new AbortController()

      loadCatalogProducts(query, controller)

      return () => controller.abort()
    } else {
      setHasLoadError(false)
      setCatalogProducts(initialCatalogProducts)
    }
  }, [initialCatalogProducts, query])

  return (
    <Autocomplete
      options={sortedOptions}
      getOptionLabel={(option) => option}
      getOptionKey={(option) => option}
      noOptionsText={hasLoadError ? t('actions:loadError') : null}
      freeSolo={true}
      clearIcon={null}
      isOptionEqualToValue={(option, value) => option === value}
      renderOption={(props, option) => <MenuItem {...props}>{option}</MenuItem>}
      renderInput={(params) => (
        <TextField
          label={label ?? t('selectCatalogProduct')}
          placeholder={placeholder}
          value={inputValue ?? value ?? ''}
          error={resolvedError}
          helperText={resolvedHelperText}
          {...params}
          // InputProps={{
          //   endAdornment: null
          // }}
        />
      )}
      value={value ?? null}
      {...props}
    />
  )
}
